import { useState } from 'react';
import Modal from 'components/core/Modals/Modal/Modal';
import { Button, Icon, Typhography } from 'components/core';
import { useGetUserQuery } from 'services/userApi';
import {
  VendorPayment,
  VendorPaymentActor,
  useApproveVendorPaymentsMutation,
  useGetVendorPaymentQuery,
  useRejectVendorPaymentsMutation,
} from 'services/vendorPaymentsApi';
import RejectReasonModal from './RejectReasonModal';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const fmtAmount = (n: number | string | null | undefined) => {
  if (n === null || n === undefined) return '—';
  const v = typeof n === 'string' ? Number(n) : n;
  return Number.isFinite(v) ? currency.format(v) : '—';
};
const fmtDate = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : null);

const statusDisplayLabel = (status: string): string => {
  if (status === 'filed') return 'Processed';
  return status.replace(/_/g, ' ');
};

const statusChipClass = (status: string) => {
  switch (status) {
    case 'pending_approval':
      return 'bg-yellow-100 text-yellow-800';
    case 'approved':
    case 'filed':
    case 'paid':
    case 'partially_paid':
      return 'bg-green-100 text-green-800';
    case 'rejected':
    case 'cancelled':
    case 'voided':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-grey-100 text-grey-600';
  }
};

const bankLast4Display = (row: VendorPayment): string => {
  const n = row.source_metadata?._sourceBankAccountNumber;
  if (typeof n === 'string' && n.length >= 4) return `•••• ${n.slice(-4)}`;
  return '—';
};

const actorLabel = (actor: VendorPaymentActor | null | undefined): string | undefined => {
  if (!actor) return undefined;
  const name = [actor.first_name, actor.last_name].filter(Boolean).join(' ').trim();
  return name || actor.email;
};

interface Props {
  rowId: string | null;
  onClose: () => void;
  onMutationDone: () => void;
}

const TimelineRow = ({
  label,
  at,
  by,
  detail,
}: {
  label: string;
  at: string | null | undefined;
  by?: string | null;
  detail?: string | null;
}) => {
  if (!at) return null;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-grey-100 last:border-b-0">
      <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 shrink-0" />
      <div className="flex-1">
        <div className="text-14s font-medium text-grey-900">{label}</div>
        <div className="text-12s text-grey-600">
          {fmtDate(at)}
          {by ? ` · by ${by}` : ''}
        </div>
        {detail && <div className="text-12s text-grey-700 mt-1 italic">{detail}</div>}
      </div>
    </div>
  );
};

const PaymentDetailModal = ({ rowId, onClose, onMutationDone }: Props) => {
  const { data: user } = useGetUserQuery({});
  const { data: row, isFetching } = useGetVendorPaymentQuery(rowId ?? '', {
    skip: !rowId,
  });
  const [approveRows, approveState] = useApproveVendorPaymentsMutation();
  const [rejectRows, rejectState] = useRejectVendorPaymentsMutation();
  const [rejectOpen, setRejectOpen] = useState(false);

  if (!rowId) return null;
  if (!row && isFetching) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        modalAdditionalClasses="!w-[720px] items-center justify-center px-6 py-10 flex-col"
      >
        <Typhography component="p" type="h2" color="text-grey-600">
          Loading payment…
        </Typhography>
      </Modal>
    );
  }
  if (!row) return null;

  const canAct =
    row.status === 'pending_approval' &&
    !!user?.id &&
    (row.assigned_approver_user_ids ?? []).includes(user.id);
  const payables = row.source_metadata?.payables ?? [];

  const handleApprove = async () => {
    await approveRows([row.id]).unwrap();
    onMutationDone();
  };
  const handleReject = async (reason: string) => {
    await rejectRows({
      ids: [row.id],
      rejection_reason: reason,
    }).unwrap();
    setRejectOpen(false);
    onMutationDone();
  };

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        modalAdditionalClasses="!w-[720px] max-h-[86vh] overflow-hidden items-center justify-center px-0 py-0 flex-col"
      >
        <div className="flex flex-col w-full max-h-[86vh]">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-grey-100">
            <div>
              <Typhography component="p" type="h2" color="text-grey-900 uppercase">
                {row.source_metadata?.vendorName ??
                  row.source_metadata?.vendorAbbreviation ??
                  'Vendor payment'}
              </Typhography>
              <div className="text-12s text-grey-600 mt-1">{row.method}</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer text-grey-600"
              aria-label="Close"
            >
              <Icon iconColor="inherit" iconType="CloseIcon" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-12s text-grey-600">Amount</div>
                <div className="text-18s font-medium">{fmtAmount(row.amount)}</div>
              </div>
              <div>
                <div className="text-12s text-grey-600">Status</div>
                <span
                  className={`inline-block px-2 py-1 rounded text-12s capitalize ${statusChipClass(
                    row.status
                  )}`}
                >
                  {statusDisplayLabel(row.status)}
                </span>
              </div>
              <div>
                <div className="text-12s text-grey-600">Approval rule</div>
                <div className="text-14s font-medium">
                  {row.matchedRule?.approver_role_label ?? '—'}
                </div>
              </div>
              <div>
                <div className="text-12s text-grey-600">Property</div>
                <div className="text-14s font-medium">{row.property?.name ?? '—'}</div>
              </div>
              <div>
                <div className="text-12s text-grey-600">Bank</div>
                <div className="text-14s font-medium">{bankLast4Display(row)}</div>
                {row.stripeMode === 'test' && (
                  <div className="text-12s text-yellow-800 mt-1">
                    test mode — will debit Stripe bank •••• 6789
                  </div>
                )}
              </div>
            </div>

            {row.rejection_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-14s text-red-800">
                <span className="font-medium">Rejection reason:</span> {row.rejection_reason}
              </div>
            )}

            {/* Payables */}
            <div>
              <div className="text-14s font-medium text-grey-900 mb-2">
                Payables ({payables.length})
              </div>
              {payables.length === 0 ? (
                <div className="text-12s text-grey-600">
                  No invoice-level detail in the ResMan payload.
                </div>
              ) : (
                <table className="w-full text-14s border border-grey-100 rounded-lg">
                  <thead className="bg-grey-50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Reference</th>
                      <th className="px-3 py-2 font-medium">Description</th>
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payables.map((p, idx) => (
                      <tr key={idx} className="border-t border-grey-100">
                        <td className="px-3 py-2 font-mono text-12s">{p.reference ?? '—'}</td>
                        <td className="px-3 py-2">{p.description ?? '—'}</td>
                        <td className="px-3 py-2">{p.date ?? '—'}</td>
                        <td className="px-3 py-2 text-right">{fmtAmount(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Timeline */}
            <div>
              <div className="text-14s font-medium text-grey-900 mb-2">Activity</div>
              <div className="border border-grey-100 rounded-lg px-3 py-1">
                <TimelineRow label="Synced from ResMan" at={row.created_at} />
                <TimelineRow
                  label="Approved"
                  at={row.approved_at}
                  by={actorLabel(row.approvedByUser)}
                />
                <TimelineRow
                  label="Rejected"
                  at={row.rejected_at}
                  by={actorLabel(row.rejectedByUser)}
                  detail={row.rejection_reason ?? undefined}
                />
                <TimelineRow
                  label="Cancelled"
                  at={row.cancelled_at}
                  by={actorLabel(row.cancelledByUser)}
                />
                <TimelineRow
                  label="Voided (by ResMan)"
                  at={row.voided_at}
                  detail="ResMan reported this Payment as voided after it was synced."
                />
                <TimelineRow label="Processed" at={row.filed_at} />
              </div>
            </div>
          </div>

          {/* Footer */}
          {canAct && (
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-grey-100 bg-grey-50">
              <Button
                type="button"
                variants="defaultOutlined"
                buttonLabel="Reject"
                onClick={() => setRejectOpen(true)}
                isDisabled={rejectState.isLoading || approveState.isLoading}
                additionalClasses="px-5 py-2"
              />
              <Button
                type="button"
                variants="default"
                buttonLabel={approveState.isLoading ? 'Approving…' : 'Approve'}
                onClick={handleApprove}
                isDisabled={rejectState.isLoading || approveState.isLoading}
                additionalClasses="px-5 py-2"
              />
            </div>
          )}
        </div>
      </Modal>

      <RejectReasonModal
        isOpen={rejectOpen}
        count={1}
        onClose={() => setRejectOpen(false)}
        onSubmit={handleReject}
        isSubmitting={rejectState.isLoading}
      />
    </>
  );
};

export default PaymentDetailModal;
